import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "util/UserContext";
import ActorsList from "./ActorsList";
import TeamsList from "components/Teams/TeamsList";
import ActionsList from "components/Actions/ActionsList";
import FollowList from "./FollowList";
import CommunityDashboardPage from "components/CommunityData/CommunityDashboardPage";
import ScrollHideContainer from "ui/ScrollHideContainer";
import ScrollableTabsList from "ui/ScrollableTabsList";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/InputSelect";
import { setDocumentTitle, setDefaultSiteOg } from "util/ogMetadataUtils";
import { getCommunityUiStrings, resolveCommunityLocale } from "i18n/communityUi";

/**
 * Top-level filter for the People tab.
 * - "everyone": loads all actor types (users + teams)
 * - "users": loads users only; signed-in viewers can refine to following/followers
 * - "parteas": loads teams; signed-in viewers can refine to teams they belong to
 */
type PeopleFilter = "everyone" | "users" | "parteas";

/** Secondary filter when "users" is selected. */
type UserSubFilter = "all" | "following" | "followers";

/** Secondary filter when "parteas" is selected. */
type ParteaSubFilter = "all" | "my";

const SUPPORTED_TABS = ["dashboard", "people", "action"] as const;
type CommuniteaTab = (typeof SUPPORTED_TABS)[number];

const DEFAULT_TAB: CommuniteaTab = "dashboard";

function normalizeTab(value: string | undefined): CommuniteaTab {
  if (value && (SUPPORTED_TABS as readonly string[]).includes(value)) {
    return value as CommuniteaTab;
  }
  return DEFAULT_TAB;
}

/**
 * Communitea is the single home for community surfaces: the executive
 * dashboard, the people directory (users + parteas + viewer-relative
 * filters), and live actions. Each segment is a tab; the People tab uses
 * dropdown filters instead of separate tabs so we don't fan out the URL space.
 */
const Community: React.FC = () => {
  const { currentUser } = useUser();
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const labels = getCommunityUiStrings(resolveCommunityLocale());

  const selectedTab = normalizeTab(tab);
  const [peopleFilter, setPeopleFilter] = useState<PeopleFilter>("everyone");
  const [userSubFilter, setUserSubFilter] = useState<UserSubFilter>("all");
  const [parteaSubFilter, setParteaSubFilter] = useState<ParteaSubFilter>("all");

  // Keep the OG title in sync with the active tab so refreshes and shared
  // links surface the right context preview.
  useEffect(() => {
    const tabLabelByValue: Record<CommuniteaTab, string> = {
      dashboard: labels.tabDashboard,
      people: labels.tabPeople,
      action: labels.tabAction,
    };
    const pageTitle = `Communitea - ${tabLabelByValue[selectedTab]}`;
    setDocumentTitle(pageTitle);
    setDefaultSiteOg({ title: pageTitle, url: window.location.href });
  }, [selectedTab, labels.tabAction, labels.tabDashboard, labels.tabPeople]);

  const tabsData = useMemo(
    () => [
      {
        label: labels.tabDashboard,
        value: "dashboard",
        route: "/communitea/dashboard",
      },
      {
        label: labels.tabPeople,
        value: "people",
        route: "/communitea/people",
      },
      {
        label: labels.tabAction,
        value: "action",
        route: "/communitea/action",
      },
    ],
    [labels.tabAction, labels.tabDashboard, labels.tabPeople]
  );

  const handleTabChange = (newTab: string): void => {
    const normalized = normalizeTab(newTab);
    navigate(`/communitea/${normalized}`);
  };

  const isSignedIn = Boolean(currentUser);
  const viewerId = currentUser?.id ?? null;

  return (
    <div className="py-0 relative">
      <ScrollHideContainer
        className="z-50 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg"
        hideOffset={57}
        threshold={56}
      >
        <div className="p-2">
          <ScrollableTabsList
            key="communitea-tabs"
            pageTitle="Communitea"
            tabs={tabsData}
            selectedTab={selectedTab}
            onTabChange={handleTabChange}
          />
        </div>
      </ScrollHideContainer>

      {/* Dashboard tab: embed the executive community dashboard inline. */}
      {selectedTab === "dashboard" && <CommunityDashboardPage />}

      {/* People tab: actors directory with dropdown filters. */}
      {selectedTab === "people" && (
        <div className="py-2 px-2 md:px-4 relative">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Select
              value={peopleFilter}
              onValueChange={(value: PeopleFilter) => {
                setPeopleFilter(value);
                // Reset secondary filters so switching the primary filter
                // never leaves a stale sub-selection visible.
                setUserSubFilter("all");
                setParteaSubFilter("all");
              }}
            >
              <SelectTrigger aria-label={labels.peopleFilterLabel} className="px-3 py-2">
                <SelectValue placeholder={labels.peopleFilterEveryone} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="everyone">{labels.peopleFilterEveryone}</SelectItem>
                <SelectItem value="users">{labels.peopleFilterUsers}</SelectItem>
                <SelectItem value="parteas">{labels.peopleFilterParteas}</SelectItem>
              </SelectContent>
            </Select>

            {peopleFilter === "users" && isSignedIn && (
              <Select
                value={userSubFilter}
                onValueChange={(value: UserSubFilter) => setUserSubFilter(value)}
              >
                <SelectTrigger aria-label={labels.userSubfilterLabel} className="px-3 py-2">
                  <SelectValue placeholder={labels.userSubfilterAll} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{labels.userSubfilterAll}</SelectItem>
                  <SelectItem value="following">{labels.userSubfilterFollowing}</SelectItem>
                  <SelectItem value="followers">{labels.userSubfilterFollowers}</SelectItem>
                </SelectContent>
              </Select>
            )}

            {peopleFilter === "parteas" && isSignedIn && (
              <Select
                value={parteaSubFilter}
                onValueChange={(value: ParteaSubFilter) => setParteaSubFilter(value)}
              >
                <SelectTrigger aria-label={labels.parteaSubfilterLabel} className="px-3 py-2">
                  <SelectValue placeholder={labels.parteaSubfilterAll} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{labels.parteaSubfilterAll}</SelectItem>
                  <SelectItem value="my">{labels.parteaSubfilterMy}</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {peopleFilter === "everyone" && (
            <ActorsList isLoading={() => {}} actorTypes={["user", "team"]} />
          )}

          {peopleFilter === "users" && userSubFilter === "all" && (
            <ActorsList isLoading={() => {}} actorTypes={["user"]} />
          )}

          {peopleFilter === "users" && userSubFilter === "following" && viewerId !== null && (
            <FollowList
              viewerId={viewerId}
              viewType="following"
              emptyTitle={labels.followingEmptyTitle}
              emptySubtitle={labels.followingEmptySubtitle}
            />
          )}

          {peopleFilter === "users" && userSubFilter === "followers" && viewerId !== null && (
            <FollowList
              viewerId={viewerId}
              viewType="followers"
              emptyTitle={labels.followersEmptyTitle}
              emptySubtitle={labels.followersEmptySubtitle}
            />
          )}

          {peopleFilter === "parteas" && parteaSubFilter === "all" && (
            <TeamsList isLoading={() => {}} onlyMemberTeams={false} />
          )}

          {peopleFilter === "parteas" && parteaSubFilter === "my" && isSignedIn && (
            <TeamsList isLoading={() => {}} onlyMemberTeams={true} />
          )}
        </div>
      )}

      {/* Action tab: live action feed. */}
      {selectedTab === "action" && (
        <div className="py-2 px-2 md:px-4 relative">
          <ActionsList initialSort="soonest" />
        </div>
      )}
    </div>
  );
};

export default Community;
